import { useEffect, useState } from 'react'
import { BOOKING_TYPES, openBooking } from '../lib/booking'
import { useWebContent } from '../lib/webPrices'
import { assetUrl } from '../lib/api'

type PriceRow = { label: string; price: string }
type TabItem =
  | { title: string; desc: string; priceList?: undefined; book?: string }   // book = klíč v BOOKING_TYPES
  | { title: string; desc?: undefined; priceList: PriceRow[]; book?: string }
type Tab = {
  id: string
  label: string
  image: string
  /** Volitelně víc fotek — zobrazí se vedle sebe místo jedné. */
  images?: string[]
  items: TabItem[]
}

const TABS: Tab[] = [
  {
    id: 'rybolov',
    label: 'Rybolov',
    image: '/img/rybolov_kapr_noc.jpg',
    items: [
      {
        title: 'Vlastní rybník 3 ha',
        desc: 'Kapr, jeseter, candát, amur, koi kapr — pravidelně dosazujeme. Tři přírodní ostrovy a klidná hladina.',
      },
      {
        title: '10 rybářských chatek u vody',
        book: 'chatka',
        desc: 'Každá chatka má svůj kus břehu. Posezení, gril, dřevo k zapůjčení.',
      },
      {
        title: 'Povolenky — vystavujeme na místě',
        desc: 'Povolenku NELZE rezervovat předem. Koupí se na místě a uhradíte hotově nebo kartou.',
      },
      {
        title: 'Ceník povolenek (2026)',
        priceList: [
          { label: '6 h',   price: '300 Kč' },
          { label: '12 h',  price: '490 Kč' },
          { label: '24 h',  price: '690 Kč' },
          { label: '2 dny', price: '1 190 Kč' },
          { label: '3 dny', price: '1 790 Kč' },
          { label: '4 dny', price: '2 290 Kč' },
          { label: '5 dní', price: '2 590 Kč' },
          { label: '6 dní', price: '2 990 Kč' },
          { label: '7 dní', price: '3 390 Kč' },
        ],
      },
    ],
  },
  {
    id: 'sport',
    label: 'Sport',
    image: '/img/gal_14.jpg',
    images: ['/img/tenis_01_ctyrhra.webp', '/img/gal_14.jpg'],   // tenis + beach vedle sebe
    items: [
      {
        title: 'Tenisové kurty',
        book: 'tenis',
        desc: '3 kurty se syntetickým povrchem — hodinové sloty za 120 Kč přes náš online rezervační systém nebo recepci.',
      },
      {
        title: 'Beach volejbal',
        book: 'beach',
        desc: '3 plážová hřiště přímo u rybníka — hodinové sloty za 120 Kč. Míč k zapůjčení 50 Kč.',
      },
      {
        title: 'Plovoucí sauna na molu',
        book: 'sauna',
        desc: 'Sauna na molu přímo nad hladinou, vytápěná kamny na dřevo, a hned vedle ochlazovací sud se studenou vodou. Procedura trvá 120 minut — na výběr blok 15:00–17:00 nebo 17:30–19:30, cena 1 600 Kč. Celá sauna je po tu dobu jen vaše.',
      },
      {
        title: 'Cyklotrasa Litovel–Bouzov',
        desc: 'Areál leží přímo na cyklotrase. Půjčovna kol Litovel 7 min autem.',
      },
    ],
  },
  {
    id: 'rodina',
    label: 'S rodinou',
    image: '/img/gal_new_24.jpg',
    items: [
      {
        title: 'CHKO Litovelské Pomoraví',
        desc: 'Lužní krajina kolem řeky Moravy — naučné stezky, brouzdaliště, klid od aut.',
      },
      {
        title: 'Hrad Bouzov',
        desc: 'Pohádkový hrad ve 20 minutách. Komentované prohlídky, dětský okruh.',
      },
      {
        title: 'Mladečské jeskyně',
        desc: 'Krasový systém s nálezy člověka cromagnonského. 5 minut autem.',
      },
      {
        title: 'Aquapark Olomouc',
        desc: '25 minut autem. Tobogány, vlnobití, parní lázně.',
      },
    ],
  },
  {
    id: 'restaurace',
    label: 'Restaurace & kuchyně',
    image: '/img/gal_13.jpg',
    items: [
      {
        title: 'Kiosek u rybníka',
        desc: 'Otevřeno denně, duben až září. Polední menu, klobásy z grilu, točené pivo, káva.',
      },
      {
        title: 'Catering na míru',
        desc: 'Svatby, oslavy, firemní akce — pečené sele, gril, koktejly. Pronájem restaurace až pro 60 hostů.',
      },
      {
        title: 'Polední menu',
        desc: 'Každý den 2 polévky + 4 hlavní jídla. Aktuální menu na týden najdete v sekci Menu.',
      },
    ],
  },
]

/** Řádek „Titulek :: popis" z adminu → položka; `book` se dědí z výchozí položky se stejným titulkem. */
function parseItems(txt: string, defaults: TabItem[]): TabItem[] {
  const out: TabItem[] = []
  for (const line of txt.split('\n')) {
    const [t, ...rest] = line.split('::')
    const title = (t || '').trim(); const desc = rest.join('::').trim()
    if (!title) continue
    const base = defaults.find((d) => d.title === title)
    out.push({ title, desc: desc || (base && 'desc' in base ? base.desc || '' : ''), book: base?.book } as TabItem)
  }
  // ceník (priceList) z výchozích položek zůstává — ceny se editují v ceníku
  for (const d of defaults) if (d.priceList) out.push(d)
  return out.length ? out : defaults
}

export function Freizeit() {
  const content = useWebContent()
  const [active, setActive] = useState<string>('rybolov')
  const base = TABS.find((t) => t.id === active) || TABS[0]
  // texty a fotky přepsané v adminu (Admin → Web)
  const override = content[`freizeit_${base.id}_items`] as string | undefined
  const imgOverride = assetUrl(content[`freizeit_image_${base.id}`] as string)
  const tab: Tab = {
    ...base,
    items: override ? parseItems(override, base.items) : base.items,
    image: imgOverride || base.image,
    images: imgOverride ? undefined : base.images,
  }

  // Odkazy z menu (#volny-cas-sport, #volny-cas-rybolov…) musí přepnout
  // záložku a dojet na sekci — dřív se jen scrollovalo a zůstal Rybolov,
  // protože prvek s tím id existuje až po přepnutí záložky.
  useEffect(() => {
    const apply = () => {
      const m = /^#volny-cas-([a-z]+)/.exec(window.location.hash)
      if (!m || !TABS.some((t) => t.id === m[1])) return
      setActive(m[1])
      setTimeout(() => document.getElementById('volny-cas')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 30)
    }
    apply()
    window.addEventListener('hashchange', apply)
    return () => window.removeEventListener('hashchange', apply)
  }, [])

  return (
    <section id="volny-cas" className="py-20 md:py-28 bg-white">
      <div className="container-x">
        <div className="max-w-3xl">
          <p className="h-eyebrow">Volný čas</p>
          <h2 className="h-display text-3xl md:text-[44px] mt-4">Co u&nbsp;nás zažijete</h2>
          <p className="mt-5 text-ink-700 text-base md:text-lg">
            Rybolov, sport, výlety s&nbsp;rodinou nebo poctivá kuchyně — vyberte si.
          </p>
        </div>

        {/* Kotvy pro odkazy z menu (#volny-cas-rybolov …) — existují vždy, záložku přepne hash efekt výše */}
        {TABS.map((t) => <span key={t.id} id={`volny-cas-${t.id}`} className="block h-0" aria-hidden="true" />)}

        {/* Tabs — underline styl */}
        <div className="mt-10 flex flex-wrap gap-x-7 gap-y-2 border-b border-ink-900/10">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`relative pb-3 text-[15px] font-semibold transition-colors ${
                active === t.id
                  ? 'text-ink-900'
                  : 'text-ink-700/70 hover:text-ink-900'
              }`}
            >
              {t.label}
              <span
                className={`absolute left-0 right-0 -bottom-px h-[2px] bg-brand-500 transition-transform origin-left ${
                  active === t.id ? 'scale-x-100' : 'scale-x-0'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="mt-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {tab.images && tab.images.length > 1 ? (
            <div className="grid grid-cols-2 gap-3">
              {tab.images.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`${tab.label} ${i + 1}`}
                  className="w-full aspect-[3/4] object-cover rounded-2xl"
                  loading="lazy"
                />
              ))}
            </div>
          ) : (
            <img
              src={tab.image}
              alt={tab.label}
              className="w-full aspect-[4/3] object-cover rounded-2xl"
            />
          )}
          <div>
            <h3 className="font-bold text-2xl text-ink-900 tracking-tight">
              {tab.label}
            </h3>
            <ul className="mt-6 space-y-5">
              {tab.items.map((it) => (
                <li key={it.title}>
                  <div className="font-semibold text-ink-900">{it.title}</div>
                  {it.desc && (
                    <p className="mt-1 text-ink-700 text-sm leading-relaxed">{it.desc}</p>
                  )}
                  {it.book && (
                    <button
                      type="button"
                      onClick={() => openBooking(BOOKING_TYPES[it.book!])}
                      className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-brand-600 hover:text-brand-700"
                    >
                      Rezervovat <span aria-hidden="true">→</span>
                    </button>
                  )}
                  {it.priceList && (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
                      {it.priceList.map((p) => (
                        <div key={p.label} className="flex items-baseline justify-between border-b border-ink-900/[0.08] pb-1">
                          <span className="text-ink-700">{p.label}</span>
                          <span className="font-bold text-ink-900 tabular-nums">{p.price}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
